import { useEffect, useState } from 'react';

import { useCan, useRefusal } from '../../lib/permissions.js';
import { Icon } from '../../components/Icon.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { SideDialog } from '../../components/team/SideDialog.js';
import { AddExistingDialog } from '../../components/references/AddExistingDialog.js';
import { AddReferenceDrawer, uploadSummaryText } from '../../components/references/AddReferenceDrawer.js';
import { AddReferenceButton, type AddMode } from '../../components/references/EmptyLibrary.js';
import { GroupsPanel } from '../../components/references/GroupsPanel.js';
import { LibraryPane } from '../../components/references/LibraryPane.js';
import { QuickAddBar } from '../../components/references/QuickAddBar.js';
import { ReferenceDetailPanel } from '../../components/references/ReferenceDetailPanel.js';
import { useReferenceAddress } from '../../components/references/address.js';
import { useInvalidateReferences, useReferenceMeta } from '../../components/references/hooks.js';
import { useBulkUpload } from '../../components/references/useBulkUpload.js';
import '../../styles/team.css';
import '../../styles/references.css';

/**
 * REFERENCES — the production's library of inspiration and research
 * (docs/superpowers/specs/2026-11-02-workspace-references-design.md §6).
 *
 * Three columns under the project's own header: Groups / Themes on the left,
 * the library in the middle, the open reference on the right. One add drawer
 * for links, notes and files; the list's whole state in the address.
 *
 * A REFERENCE IS STILL NOT A CANDIDATE. Linking one to a place, a scene or an
 * element records an association and changes nothing about that item — no
 * shortlist, no approval, no booking. Nothing here is contacted or held.
 *
 * ONE RED BUTTON. With references in the library it is the header's Add
 * reference; in an empty library it is the centre's Add your first reference,
 * and the header's turns neutral (owner-approved mockups, 2026-11-02).
 */
export function ReferencesTab({ projectId }: { projectId: string }) {
  const { address, update } = useReferenceAddress();
  const meta = useReferenceMeta(projectId);
  const canEdit = useCan('REFERENCE_EDIT');
  const refusal = useRefusal('REFERENCE_EDIT');
  const invalidate = useInvalidateReferences(projectId);
  const bulk = useBulkUpload(projectId, meta.data?.upload, () => {
    void invalidate();
  });

  const [drawer, setDrawer] = useState<{ mode: AddMode; url?: string; groupIds: string[] } | null>(null);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [addingExisting, setAddingExisting] = useState(false);

  const groups = meta.data?.groups ?? null;
  const group = address.group === null ? null : (groups?.find((g) => g.id === address.group) ?? null);
  const libraryEmpty = meta.data !== undefined && meta.data.total === 0;

  // A group deleted elsewhere (or a stale link) would otherwise filter the list down
  // to nothing with no way back: once the groups are known, a missing one leaves the address.
  const staleGroup = groups !== null && address.group !== null && group === null;
  useEffect(() => {
    if (staleGroup) update({ group: null });
  }, [staleGroup, update]);

  const openDrawer = (mode: AddMode, seed: { url?: string; files?: File[] } = {}) => {
    if (seed.files && seed.files.length > 0) bulk.add(seed.files);
    // The open group is preselected — only while it still exists, so a stale address sends no dead id.
    setDrawer({ mode, ...(seed.url ? { url: seed.url } : {}), groupIds: group === null ? [] : [group.id] });
  };

  const closeDrawer = () => {
    // A finished batch is cleared; one with failures stays so they can be retried.
    if (bulk.summary.active === 0 && bulk.summary.failed === 0) bulk.reset();
    setDrawer(null);
  };

  const organize = () => {
    setCreatingGroup(true);
    if (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 1199px)')?.matches === true) setGroupsOpen(true);
  };

  const selectGroup = (groupId: string | null) => {
    update({ group: groupId });
    setGroupsOpen(false);
  };

  const groupsPanel = (testId: string) => (
    <GroupsPanel
      projectId={projectId}
      groups={groups}
      total={meta.data?.total ?? null}
      selected={address.group}
      onSelect={selectGroup}
      canEdit={canEdit}
      creating={creatingGroup}
      onCreatingChange={setCreatingGroup}
      testId={testId}
    />
  );

  const uploading = bulk.summary.active + bulk.summary.queued;

  return (
    <>
      <section className="ref-page" data-testid="references-tab">
        <PageHeader
          title="References"
          lede="Inspiration and practical material for this production: visual ideas, real locations, documents and research."
          actions={
            canEdit ? (
              <AddReferenceButton
                label="Add reference"
                primary={!libraryEmpty}
                onAdd={(mode) => {
                  openDrawer(mode);
                }}
                testId="ref-header-add"
              />
            ) : undefined
          }
        />

        {canEdit ? (
          <QuickAddBar upload={meta.data?.upload} onOpen={openDrawer} />
        ) : refusal !== null ? (
          <p className="ref-quiet" data-testid="ref-readonly">
            {refusal}
          </p>
        ) : null}

        {drawer === null && (uploading > 0 || bulk.summary.failed > 0) ? (
          <p className="ref-upload-banner" role="status" data-testid="ref-upload-banner">
            <Icon name="upload" size={15} />
            <span>{uploadSummaryText(bulk.summary)}</span>
            <button
              type="button"
              className="ref-text-btn"
              data-testid="ref-upload-banner-open"
              onClick={() => {
                setDrawer({ mode: 'UPLOAD', groupIds: [] });
              }}
            >
              Show uploads
            </button>
          </p>
        ) : null}

        <div className="ref-layout" data-detail={address.ref === null ? 'closed' : 'open'} data-testid="ref-layout">
          <aside className="ref-groups" aria-label="Groups and themes">
            {groupsPanel('ref-groups')}
          </aside>

          <section className="ref-library" aria-label="Library">
            <LibraryPane
              projectId={projectId}
              address={address}
              update={update}
              meta={meta.data}
              group={group}
              canEdit={canEdit}
              refusal={refusal}
              onAdd={(mode) => {
                openDrawer(mode);
              }}
              onOrganize={organize}
              onAddExisting={() => {
                setAddingExisting(true);
              }}
              onOpenGroups={() => {
                setGroupsOpen(true);
              }}
            />
          </section>

          <aside
            className="ref-detail"
            aria-label="Reference"
            data-open={address.ref === null ? undefined : '1'}
            data-testid="ref-detail"
          >
            {address.ref === null ? (
              <div className="ref-detail-placeholder" data-testid="ref-detail-placeholder">
                <Icon name="chat" size={34} strokeWidth={1.4} />
                <p className="ref-detail-placeholder-title">Select a reference</p>
                <p className="ref-quiet">Open a reference from the list to see its details, tags and discussion.</p>
              </div>
            ) : (
              <ReferenceDetailPanel
                key={address.ref}
                projectId={projectId}
                referenceId={address.ref}
                groups={groups ?? []}
                tagSuggestions={(meta.data?.tags ?? []).map((t) => t.label)}
                onClose={() => {
                  update({ ref: null });
                }}
                onDeleted={() => {
                  update({ ref: null });
                }}
              />
            )}
          </aside>
        </div>
      </section>

      {drawer !== null ? (
        <AddReferenceDrawer
          projectId={projectId}
          initialMode={drawer.mode}
          initialUrl={drawer.url}
          initialGroupIds={drawer.groupIds}
          meta={meta.data}
          bulk={bulk}
          onCreated={(id) => {
            update({ ref: id }, { push: true });
          }}
          onClose={closeDrawer}
        />
      ) : null}

      {groupsOpen ? (
        <SideDialog
          title="Groups"
          testId="ref-groups-drawer"
          onClose={() => {
            setGroupsOpen(false);
          }}
        >
          {groupsPanel('ref-groups-drawer-panel')}
        </SideDialog>
      ) : null}

      {addingExisting && group !== null ? (
        <AddExistingDialog
          projectId={projectId}
          group={group}
          onClose={() => {
            setAddingExisting(false);
          }}
        />
      ) : null}
    </>
  );
}
